import { AGGREGATOR_ID } from './aggregator'

// Backend path for authentication
export const API_AUTH_PATH = {
  ADD_USER: '/users',
  LOGIN: '/login',
  VERIFY_OTP: '/verify-otp',
  //  To Verify Token Received from email
  VERIFY_ID: '/verify',
  //  To Verify Auth Token received while logging in
  VERIFY_TOKEN: '/token',
  FORGOT_PASSWORD: '/forgot',
  RESET_PASSWORD: '/reset',
  LOGOUT: '/logout',
}

// Server routes for user management
export const API_USER_MGMT = {
  USER: '/users',
  UPDATE_COLS: '/users/cols',
  PICKER_ALLOWED_PAGES: '/users/picker-allowed-pages',
  // Generic form of the above - returns the pages any given role may hold
  ALLOWED_PAGES: '/users/allowed-pages',
  // Category options for the user form. Served under user management, not
  // /fresh-category, which needs category-management rights.
  ASSIGNABLE_FRESH_CATEGORIES: '/users/fresh-categories',
}

export const API_FRESH_REPORT = {
  REPORT: '/fresh-report',
  CATEGORIES: '/fresh-report/categories',
  EXPORT: '/fresh-report/export',
}

export const API_FRESH_MANDATORY_BARCODE = {
  LIST: '/fresh-mandatory-barcode',
  IMPORT: '/fresh-mandatory-barcode/import',
}

export const API_FOC_ORDERS = {
  ORDER: '/foc-order',
  BARCODE_PRICE: '/foc-order/price',
  EXPORT: '/foc-order/export',
  ORDERS_SYNC: '/foc-order/sync-erp',
}

export const API_STORE_MGMT = {
  STORE: '/store',
}

export const API_AGGREGATOR_MGMT = {
  AGGREGATOR: '/aggregator',
  DELIVEROO: `/aggregator/${AGGREGATOR_ID.deliveroo}`,
}
export const API_AREAS_MGMT = {
  AREAS: '/areas-mapping',
  POS_AREAS: '/pos-areas-mapping',
  POS_AREA_COORDS: '/pos-areas-mapping/pos-area-coords',
  AREAS_COORDS: '/areas-mapping/area-coords',
}

export const API_ADMIN = {
  CLONE_INVENTORY: '/admin/clone-inventory',
  CLONE_PRICE: '/admin/clone-pricing',
  CLONE_PRESIGNED_URL: '/admin/clone-presigned-url',
}

export const ERP_COST_MGMT = {
  ERP_COST: '/erp-cost',
}
export const API_FTP_PROFILE = {
  FTP_PROFILE: '/ftp-profile',
}

export const API_CONFIG = {
  CONFIG: '/config',
  RMA: '/config/rma',
  CLUSTERS: '/config/clusters',
}

export const API_PRICE_LABLE = {
  PRICE_LABEL: '/price-label',
  IMPORT: '/price-label/import',
  EXPORT: '/price-label/export',
}

export const API_PRICE_LABEL_REPORT = {
  PRICE_LABEL_REPORT: '/price-label-report',
  EXPORT: '/price-label-report/export',
}

export const API_STOCK_OUT_REPORT = {
  STOCK_OUT_REPORT: '/stock-out-report',
  EXPORT: '/stock-out-report/export',
}

export const API_PRICE_LABEL_SYNC_HISTORY = {
  SYNC_HISTORY: '/price-label-sync-history',
  MARK_SEEN: '/price-label-sync-history/mark-seen',
  UNSEEN_COUNT: '/price-label-sync-history/unseen-count',
  EXPORT: '/price-label-sync-history/export',
}

export const API_NEW_SAP_BARCODE = {
  NEW_SAP_BARCODE: '/new-sap-barcode',
  MARK_SEEN: '/new-sap-barcode/mark-seen',
  UNSEEN_COUNT: '/new-sap-barcode/unseen-count',
  EXPORT: '/new-sap-barcode/export',
}

// Stores can be a single id or a list — always sent as one comma separated
// path segment, which the API normalizes back into an array.
const toStoreSegment = (stores: string[] | string) =>
  (Array.isArray(stores) ? stores : [stores])
    .map((store) => encodeURIComponent(store))
    .join(',')

export const TIMESTAMP_RESET_URLS = {
  IMPORT_MASTER: '/timestamp-reset/import-master',
  IMPORT_INVENTORY: (stores: string[]) =>
    `/timestamp-reset/import-inventory/${toStoreSegment(stores)}`,
  RESET_INVENTORY: (stores: string[]) =>
    `/timestamp-reset/reset-inventory/${toStoreSegment(stores)}`,
  IMPORT_PRICING: (stores: string[], aggregator: string) =>
    `/timestamp-reset/import-pricing/${toStoreSegment(
      stores,
    )}/${encodeURIComponent(aggregator)}`,
  RESET_PRICING: (stores: string[], aggregator: string) =>
    `/timestamp-reset/reset-pricing/${toStoreSegment(
      stores,
    )}/${encodeURIComponent(aggregator)}`,
}

export const API_IMPORT = {
  TEMPLATE: '/import/template',
  SAMPLE: '/import/sample',
}

export const API_MASTER_DATA = {
  MASTER_DATA: '/catalogue',
  MASTER_DATA_EXPORT: '/catalogue/export',
  MASTER_DATA_IMPORT: '/catalogue/import',
  MASTER_DATA_IMPORT_CATEGORIES: '/catalogue/import-categories',
  MASTER_DATA_BARCODE_SEARCH: '/catalogue/barcode-search',
  MASTER_DATA_FRESH_CATEGORIES: '/catalogue/fresh-categories',
}

export const API_BUNDLE_PRODUCT = {
  BUNDLE_PRODUCT: '/bundle-product',
  IMPORT: '/bundle-product/import',
  EXPORT: '/bundle-product/export',
}

export const API_UPLOAD = {
  PRESIGNED_URL: '/file-upload/presigned-url',
}

export const API_INVENTORY = {
  INVENTORY: '/inventory',
  INVENTORY_EXPORT: '/inventory/export',
  INVENTORY_IMPORT: '/inventory/import',
  CALL_BY_INVENTORY: '/inventory/callBy/inventory',
  ERP_WINDOW_UPDATE_BY_STORES: '/inventory/erp-window-update/stores',
  ERP_QTY_RESET_BARCODES_IMPORT: '/inventory/import-erpqty-reset',
  ERP_QTY_RESET_BARCODES_EXPORT: '/inventory/export-erpqty-reset',
  INVENTORY_STOCK_CHECKER: '/inventory/inventory-checker',
  INVENTORY_BARCODE_SEARCH: '/inventory/barcode-search',
}

// Server Routes for Logs
export const API_LOGS = {
  ERP_LOGS: '/log/erp',
  ERP_LOGS_DOWNLOAD: '/log/erp/download',
  POS_LOGS: '/log/pos',
  POS_LOGS_DOWNLOAD: '/log/pos/download',
  FTP_LOGS: '/log/ftp',
  FTP_LOGS_DOWNLOAD: '/log/ftp/download',
  IMPORTS_LOGS: '/log/imports',
  EXPORT_LOGS: '/log/export',
  ORDERS_LOGS: '/log/orders',
  RECONCILIATION_LOGS: '/log/reconciliation',
  TALON_LOGS: '/log/talon',
  TALON_LOGS_DOWNLOAD: '/log/talon/download',
  RECONCILIATION_LOGS_DOWNLOAD: '/log/reconciliation/download',
  ORDERS_LOGS_DOWNLOAD: '/log/orders/download',
  IMPORTS_LOGS_DOWNLOAD: '/log/imports/download',
  EXPORT_LOGS_DOWNLOAD: '/log/export/download',
  AGGREGATOR_LOGS: '/log/aggregator',
  AGGREGATOR_LOGS_DOWNLOAD: '/log/aggregator/download',
  SAP_LOGS: '/log/sap',
  SAP_LOGS_DOWNLOAD: '/log/sap/download',
  ADMIN_AUDIT_LOGS: '/log/admin-audit',
  ADMIN_AUDIT_LOGS_DOWNLOAD: '/log/admin-audit/download',
  DELIVEROO_SYNC_LOGS: '/log/deliveroo-sync',
  DELIVEROO_SYNC_LOGS_DOWNLOAD: '/log/deliveroo-sync/download',
  TALABAT_LOGS: '/log/talabat-sync',
  TALABAT_LOGS_DOWNLOAD: '/log/talabat-sync/download',
  CATALOGUE_ACTION_LOGS: '/log/catalogue-sync',
  CATALOGUE_ACTION_LOGS_DOWNLOAD: '/log/catalogue-sync/download',
  POWER_BI_SYNC_LOGS: '/log/power-bi-sync',
  POWER_BI_SYNC_LOGS_DOWNLOAD: '/log/power-bi-sync/download',
  KDS_SYNC_LOGS: '/log/kds-sync',
  LAST_MILE_LOGS: '/log/last-mile',
  LAST_MILE_LOGS_DOWNLOAD: '/log/last-mile/download',
  GENERIC_LOGS: '/log/generic',
  KDS_SYNC_LOGS_DOWNLOAD: '/log/kds-sync/download',
}

// server file download path
export const API_FILE_DOWNLOAD_PATH = {
  DOWNLOAD_CSV: '/export-file/download-csv',
}

// Server Routes for Smart OOS
export const API_SMART_OOS = {
  UPLOAD: '/smart-oos/upload',
  SMART_OOS: '/smart-oos',
}

// Server Routes for Pricing
export const API_PRICING = {
  PRICING: '/pricing',
  PRICING_IMPORT: '/pricing/import',
  ECOM_PRICING: '/ecom-pricing',
  ECOM_PRICING_IMPORT: '/ecom-pricing/import',
  ECOM_PRICING_EXPORT: '/ecom-pricing/export',
  ONLINE_PRICING_IMPORT: '/pricing/import/online-price',
  AGGREGATOR_ONLINE_PRICING_IMPORT: '/pricing/import/aggregator-online-price',
  CALL_BY_PRICE: '/pricing/call/price',
  PRICING_EXPORT: '/pricing/export',
  INVALID_PRICE_REPORT: '/pricing/invalid-report',
  UPDATED_ERP_PRICE_REPORT: '/pricing/erp-report',
  PRICING_BARCODE_SEARCH: '/pricing/barcode-search',
}

// Server routes for function and jobs
export const FUNCTION_JOBS = {
  IMPORT_FTP_BARCODES: '/function-jobs/import-ftp-barcodes',
  EXPORT_FTP_BARCODES: '/function-jobs/export-ftp-barcodes',
  TRIGGER_AGGREGATOR_API: '/function-jobs/aggregator-api',
}

export const API_CRON = {
  CRON_FRESH_SYSTEM: 'cron/fresh-system',
  CRON_PRICING: 'cron/pricing',
  CRON_SYNC_AGG: 'cron/sync-aggregator-pricing',
  CRON_SYNC_EMP_ID: 'cron/emp-id-sync',
  CRON_MAGENTO_CATALOGUE: 'cron/magento-catalogue-sync',
}

export const API_TALABAT = {
  PRODUCT_UPDATE: '/aggregator/EXT-TALABAT/sync-product-update',
  PROMOTION_UPDATE: '/aggregator/EXT-TALABAT/sync-promotions',
  POLL_PROMOTION: '/aggregator/EXT-TALABAT/sync-promotions-logs',
}

export const API_ORDER = {
  ORDERS: '/order',
  OPEN_ORDERS: '/order/open',
  ORDERS_BY_STORE: '/order/store',
  ORDERS_EXPORT: '/order/export',
  IMPORT: '/order/import',
  ORDERS_SYNC: '/order/sync',
  RECONCILIATION_SYNC: '/order/reconciliation-sync',
  RMA_SYNC: '/order/rma-sync',
  LASTMILE_SYNC: '/order/lastmile-sync',
  GENERATE_REPORT: '/order/generate-report',
  STORE_SALES_REPORT: '/order/export-store-sales-report',
}

export const API_RMA = {
  RMA_EXPORT: '/rma/export',
}

export const API_PENDING_ERP_SYNC = {
  ORDER: '/pending-erp-sync/order',
  RMA: '/pending-erp-sync/rma',
  RECON: '/pending-erp-sync/reconciliation',
}

export const API_PICKER_EMP_MAPPING = {
  PICKER_MAP: '/picker-emp-map',
  IMPORT: '/picker-emp-map/import',
  EXPORT: '/picker-emp-map/export',
  PICKERS: '/picker-emp-map/pickers',
  PICKERS_FULL_SYNC: '/picker-emp-map/pickers/full-sync',
}

export const API_PHARMACY = {
  IMPORT_CATALOGUE: '/pharmacy/import',
  DELETE_STORE_ASSOCIATION: '/pharmacy/delete-store',
}

export const API_CHAIN_MGMT = {
  CHAIN: '/chain',
}

export const POLYGON_API_PATH = {
  POLYGON_SYNC: '/instashopLastmile/sync-polygons',
}

export const API_POWER_BI = {
  CONFIG: '/config/power-bi',
  SYNC: '/config/power-bi/sync',
  RMA_SYNC: '/config/power-bi/rma-sync',
}

export const API_ALERT_CONFIG = {
  CONFIG: '/config/alert',
}

export const API_CRON_JOBS = {
  LIST: '/cron-jobs',
  RESET: (name: string) => `/cron-jobs/${name}/reset`,
  UPDATE: (name: string) => `/cron-jobs/${name}`,
}

export const API_ORDER_TRACKER = {
  CONFIG: '/config/order-tracker',
}

export const API_KDS = {
  CONFIG: '/config/kds',
  SYNC: '/config/kds/sync',
}

export const API_MAGENTO = {
  CONFIG: '/config/magento',
  SYNC: '/config/magento/sync',
}

export const API_INSTASHOP_LASTMILE = {
  CONFIG: '/config/instashop-lastmile',
  RECONCILIATION_SYNC: '/config/instashop-lastmile/reconciliation/sync',
}

export const API_PURCHASE_TIMER = {
  PURCHASE_TIMER: '/purchase-timer',
  EXPORT: '/purchase-timer/export',
  IMPORT: '/purchase-timer/import',
}

export const API_FRESH_CATEGORY = {
  CATEGORY: '/fresh-category',
}
